/**
 * Inking canvas stamp — per-pixel brush dab over an RGBA raster buffer.
 *
 * Mirrors the sketch stroke stamp algorithm (hardness falloff,
 * premultiplied over-compositing, eraser as alpha reduction), but works
 * directly on a 2D pixel grid in page-pixel space instead of per-triangle
 * texel patches.
 */

export interface InkingStampParams {
  /** Center in raster pixel coordinates. */
  x: number;
  y: number;
  /** Radius in raster pixels. */
  radius: number;
  /** Pressure [0-1]; the handler pre-modulates size or flow by pressureMode. */
  pressure: number;
  /** Edge hardness [0-1]: 1 = sharp, 0 = fully feathered. */
  hardness: number;
  /** Brush color (0-1 floats). */
  cr: number;
  cg: number;
  cb: number;
  /** Stroke opacity [0-1]. Multiplies the dab's effective flow. */
  opacity: number;
  /** Dab flow spacing factor (~0.15 for a sketch-style stroke). */
  spacing: number;
  /** Eraser mode — reduces alpha instead of compositing color. */
  erase: boolean;
}

/**
 * Apply a single dab to the raster in-place. Returns true if any pixels were
 * modified (so callers can skip redraws when a dab fell entirely outside the
 * buffer or below the visibility threshold).
 *
 * Bounding-box iteration keeps the inner loop cheap — a 40 px radius touches
 * ~5000 pixels per dab, comfortably within a single frame on the main thread.
 */
export function stampInkingRaster(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  p: InkingStampParams,
): boolean {
  if (p.radius <= 0) return false;

  const r = p.radius;
  const r2 = r * r;
  const xMin = Math.max(0, Math.floor(p.x - r));
  const yMin = Math.max(0, Math.floor(p.y - r));
  const xMax = Math.min(width - 1, Math.ceil(p.x + r));
  const yMax = Math.min(height - 1, Math.ceil(p.y + r));
  if (xMin > xMax || yMin > yMax) return false;

  const dabFlow =
    1 - (1 - Math.max(0, Math.min(1, p.pressure * p.opacity))) ** p.spacing;
  if (dabFlow <= 0) return false;

  const edge = 1 - p.hardness;
  const cr255 = p.cr * 255;
  const cg255 = p.cg * 255;
  const cb255 = p.cb * 255;

  let modified = false;

  for (let y = yMin; y <= yMax; y++) {
    const dy = y + 0.5 - p.y;
    for (let x = xMin; x <= xMax; x++) {
      const dx = x + 0.5 - p.x;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) continue;

      let weight = 1;
      if (p.hardness < 1) {
        const t = Math.sqrt(d2) / r;
        const w = Math.max(0, Math.min(1, (1 - t) / edge));
        weight = w * w;
      }
      const alpha = dabFlow * weight;
      if (alpha <= 0) continue;

      const idx = (y * width + x) * 4;
      if (p.erase) {
        data[idx + 3] = Math.round(data[idx + 3] * (1 - alpha));
      } else {
        const srcA = alpha;
        const dstA = data[idx + 3] / 255;
        const outA = srcA + dstA * (1 - srcA);
        if (outA <= 0) continue;
        const invOut = 1 / outA;
        data[idx] = Math.round(
          (cr255 * srcA + data[idx] * dstA * (1 - srcA)) * invOut,
        );
        data[idx + 1] = Math.round(
          (cg255 * srcA + data[idx + 1] * dstA * (1 - srcA)) * invOut,
        );
        data[idx + 2] = Math.round(
          (cb255 * srcA + data[idx + 2] * dstA * (1 - srcA)) * invOut,
        );
        data[idx + 3] = Math.round(outA * 255);
      }
      modified = true;
    }
  }

  return modified;
}

/**
 * Interpolate dabs between two positions at `spacingPx` pixel intervals.
 * Returns the number of dabs committed. `prev` is updated in place to the
 * last dab actually stamped (for continuity across subsequent moves).
 */
export function stampInkingSegment(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  prev: { x: number; y: number; pressure: number } | null,
  next: { x: number; y: number; pressure: number },
  params: Omit<InkingStampParams, "x" | "y" | "pressure">,
  spacingPx: number,
): { committed: number; anchor: { x: number; y: number; pressure: number } } {
  if (!prev) {
    stampInkingRaster(data, width, height, {
      ...params,
      x: next.x,
      y: next.y,
      pressure: next.pressure,
    });
    return { committed: 1, anchor: { ...next } };
  }
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const dist = Math.hypot(dx, dy);
  if (dist < spacingPx) {
    return { committed: 0, anchor: prev };
  }
  const numDabs = Math.floor(dist / spacingPx);
  for (let i = 1; i <= numDabs; i++) {
    const t = Math.min(i / numDabs, (i * spacingPx) / dist);
    const px = prev.x + dx * t;
    const py = prev.y + dy * t;
    const pp = prev.pressure + (next.pressure - prev.pressure) * t;
    stampInkingRaster(data, width, height, {
      ...params,
      x: px,
      y: py,
      pressure: pp,
    });
  }
  const tFinal = (numDabs * spacingPx) / dist;
  return {
    committed: numDabs,
    anchor: {
      x: prev.x + dx * tFinal,
      y: prev.y + dy * tFinal,
      pressure: next.pressure,
    },
  };
}
